/* What the link in an email left behind in the address bar.
 *
 * Supabase hands the outcome back in the URL rather than in a response, and
 * it uses two different places depending on the flow: the part after the #
 * for the older style, the query string for the newer one. Both are read
 * here so the app does not care which it gets.
 *
 * This matters because a password reset link works exactly once. If it has
 * already been spent, Supabase says so in the URL and nowhere else. Without
 * reading it the app can only put the sign-in card back on screen, which
 * looks identical to nothing having happened, and the person tries again
 * with a link that can never work.
 */

const RECOVERY_ERRORS = /otp_expired|invalid|expired/i;

/* Reads both halves of the address bar into one bag of values. */
function values({ hash = "", search = "" }) {
  const bag = new URLSearchParams();
  for (const part of [hash.replace(/^#/, ""), search.replace(/^\?/, "")]) {
    if (!part) continue;
    for (const [k, v] of new URLSearchParams(part)) bag.set(k, v);
  }
  return bag;
}

export function readAuthLink(location = {}) {
  const bag = values(location);
  const type = bag.get("type") ?? "";
  const errorCode = bag.get("error_code") ?? bag.get("error") ?? "";
  const description = (bag.get("error_description") ?? "").replace(/\+/g, " ");

  const recovery = type === "recovery"
    || (bag.has("access_token") && type === "recovery")
    || (bag.has("code") && type === "recovery");

  if (!errorCode) return { recovery, error: "" };

  /* The one that actually happens, and the one worth explaining properly.
     Microsoft and Google both open links in incoming mail to check them
     before the person does, which spends a single use link on their behalf. */
  if (RECOVERY_ERRORS.test(errorCode) || RECOVERY_ERRORS.test(description)) {
    return {
      recovery: false,
      error: "That reset link has already been used or has expired. Links work once and last an hour. If you never clicked it, your email system most likely opened it first to check it was safe, which uses it up. Ask for a new one and open it within the hour, or forward the email to a personal address and open it from there.",
    };
  }

  return {
    recovery: false,
    error: description || "That link did not work. Ask for a new one.",
  };
}

/* Read once, at load, before anything has a chance to tidy the address bar.
   Supabase clears the hash as soon as it has read it, so waiting until a
   component mounts is too late. */
const atLoad = typeof window !== "undefined"
  ? readAuthLink({ hash: window.location.hash, search: window.location.search })
  : { recovery: false, error: "" };

export const linkAtLoad = atLoad;

/* Takes the tokens out of the address bar so a refresh does not replay a
   link that is already spent. */
export function tidyUrl(win = typeof window !== "undefined" ? window : null) {
  if (!win?.history?.replaceState) return;
  const { pathname } = win.location;
  if (win.location.hash || win.location.search) {
    win.history.replaceState({}, "", pathname);
  }
}
